import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { SubscriptionController, SubscriptionStatus } from "../../api/controllers/subscription.controller";

interface SubscriptionState {
  // Core subscription data
  isSubscribed: boolean;
  isPremium: boolean;
  isPastDue: boolean;
  monthlyFallbackOffered: boolean;
  isTrialing: boolean;
  plan: string | null;
  status: SubscriptionStatus['status'];
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;

  // Loading state
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
}

const initialState: SubscriptionState = {
  isSubscribed: false,
  isPremium: false,
  isPastDue: false,
  monthlyFallbackOffered: false,
  isTrialing: false,
  plan: null,
  status: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  trialEnd: null,
  loading: false,
  error: null,
  lastFetched: null,
};

// Async thunk to fetch subscription status from API
export const fetchSubscription = createAsyncThunk(
  "subscription/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await SubscriptionController.getStatus();
      return response.data;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } };
      return rejectWithValue(axiosError.response?.data?.error || "Failed to fetch subscription status");
    }
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setSubscription: (state, action: PayloadAction<SubscriptionStatus>) => {
      const data = action.payload;
      state.isSubscribed = data.isSubscribed;
      state.isPremium = data.isPremium;
      state.isPastDue = data.isPastDue || false;
      state.monthlyFallbackOffered = data.monthlyFallbackOffered || false;
      state.isTrialing = data.status === 'trialing';
      state.plan = data.plan;
      state.status = data.status;
      state.currentPeriodEnd = data.currentPeriodEnd;
      state.cancelAtPeriodEnd = data.cancelAtPeriodEnd;
      state.trialEnd = data.trialEnd;
      state.lastFetched = Date.now();
      state.error = null;
    },
    clearSubscription: (state) => {
      state.isSubscribed = false;
      state.isPremium = false;
      state.isPastDue = false;
      state.monthlyFallbackOffered = false;
      state.isTrialing = false;
      state.plan = null;
      state.status = null;
      state.currentPeriodEnd = null;
      state.cancelAtPeriodEnd = false;
      state.trialEnd = null;
      state.lastFetched = null;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        const data = action.payload;
        state.isSubscribed = data.isSubscribed;
        state.isPremium = data.isPremium;
        state.isPastDue = data.isPastDue || false;
        state.monthlyFallbackOffered = data.monthlyFallbackOffered || false;
        state.isTrialing = data.status === 'trialing';
        state.plan = data.plan;
        state.status = data.status;
        state.currentPeriodEnd = data.currentPeriodEnd;
        state.cancelAtPeriodEnd = data.cancelAtPeriodEnd;
        state.trialEnd = data.trialEnd;
        state.lastFetched = Date.now();
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // IMPORTANT: Do NOT reset isPremium/isSubscribed on API failure.
        // If a premium user's network request fails, they should keep
        // their cached premium status rather than being locked out.
        // UPDATE: We MUST update lastFetched on failure to prevent infinite retry loops.
        state.lastFetched = Date.now();
      });
  },
});

export const { setSubscription, clearSubscription, setError } = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
